/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

// File ACL levels enum
export enum FileAclLevel {
  NONE = 'NONE',
  BROWSE = 'BROWSE',
  VIEW = 'VIEW',
  READ = 'READ',
  WRITE = 'WRITE',
  DELETE = 'DELETE'
}

// System helper types for distinguishing users and groups
export enum SystemHelperType {
  USER = 'USER',
  GROUP = 'GROUP'
}

// User/Group entity for ACL assignments
export interface UserGroupEntity {
  id: number
  name: string
  systemHelperType: SystemHelperType
  firstName?: string // For users
  lastName?: string // For users
  accountName?: string // For users
  email?: string // For users
  groupName?: string // For groups
}

// ACL assignment for a specific level
export interface AclAssignment {
  level: FileAclLevel
  merged: UserGroupEntity[] // Combined users and groups assigned to this level
  users: UserGroupEntity[] // Only users assigned to this level
  groups: UserGroupEntity[] // Only groups assigned to this level
  availableUsersGroups: UserGroupEntity[] // All available users/groups for assignment
  editing: boolean // UI state for editing mode
}

// File System Information (FSI) reference
export interface FileSystemInfo {
  id: number
  name: string
  path?: string
  type?: string
  parentId?: number
}

// ACL update request payload
export interface AclUpdateRequest extends Record<string, unknown> {
  applyOnlyChanges: boolean
  applyRecursively: boolean
  groupAcls: Record<FileAclLevel, number[]>
  userAcls: Record<FileAclLevel, number[]>
}

// API response for file ACLs
export interface FileAclResponse {
  userAcls: Record<FileAclLevel, string[]>
  groupAcls: Record<FileAclLevel, string[]>
}

// API response for assembled users and groups
export interface AssembledUsersGroupsResponse {
  merged: UserGroupEntity[]
  users: UserGroupEntity[]
  groups: UserGroupEntity[]
}

// ACL comparison result for level checking
export interface AclComparisonResult {
  isAbove: boolean
  aclIndex: number
  minIndex: number
  acl: FileAclLevel
  minLevel: FileAclLevel
}

// ACL service error types
export interface AclServiceError {
  error: boolean
  message?: string
  code?: string | number
  details?: Record<string, unknown>
}

// Options for ACL operations
export interface AclUpdateOptions {
  recursively?: boolean
  onlyChanges?: boolean
}

// ACL validation result
export interface AclValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

// Complete ACL state for a file
export interface FileAclState {
  fsi: FileSystemInfo
  acls: AclAssignment[]
  isLoading: boolean
  hasChanges: boolean
  lastUpdated?: Date
}

// ACL service configuration
export interface AclServiceConfig {
  apiBaseUrl?: string
  enableCaching?: boolean
  cacheTimeout?: number
}

// Hook return types
export interface UseFileAclResult {
  acls: AclAssignment[]
  isLoading: boolean
  error: Error | null
  updateAcls: (acls: AclAssignment[], options?: AclUpdateOptions) => Promise<void>
  refreshAcls: () => Promise<void>
  hasChanges: boolean
  resetChanges: () => void
}

export interface UseAclValidationResult {
  isAbove: (acl: FileAclLevel, minLevel: FileAclLevel) => boolean
  validateAcls: (acls: AclAssignment[]) => AclValidationResult
  getAvailableLevels: () => FileAclLevel[]
  compareAcls: (acl: FileAclLevel, minLevel: FileAclLevel) => AclComparisonResult
}
